import { BlockError } from '@/blocks.04-project/block-error/block-error';
import toNumber from 'lodash-es/toNumber';
import isNull from 'lodash-es/isNull.js';
import isUndefined from 'lodash-es/isUndefined';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';

/***
 * Класс каунтера
 * @example
 *
 * Методы:
 * update({ step, max, min, float, mask, roundByStep }) - обновление параметров каунтера. Подробнее в описании
 *
 * Аттрибуты:
 * data-max - {number|null, default = Infinity} - максимальное число. Если null то Infinity
 * data-min - {number|null, default = -Infinity} - минимальное число. Если null то -Infinity
 * data-step - {number|null, default = 1} - шаг числа. Если null то 1
 * data-float - {number|null, default = 0} - цифр после запятой. Если null то 0
 * data-round-by-step - {boolean, default = false} - нужно ли округление до шага после ручного ввода пользователя
 *
 * Аттрибуты на элементах:
 * data-control-plus - вешается на кнопку plus
 * data-control-minus - вешается на кнопку minus
 * data-inputmask - библиотека маски
 *
 * Классы:
 * .counter.disabled - вешается если input имеет аттрибут disabled (Проверка после события change и при инициализации)
 */
class Counter {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с классом counter
   * @param force {Boolean} принудительно инициализирование
   */
  constructor(elem, force) {
		this.BlockError = new BlockError({ blockName: 'Counter', destroy: this.destroy });

		if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.Counter = this;
    this.block.AsyncCounter = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'counter_inited' });
    this.LazyInit.init();
    if (force) this.LazyInit.earlyInit();
  }

  init = async () => {
    await window.Loading.Inputmask.catch((err) => {
      throw new Error(err);
    });

    this.setOptions();

    this.updateMask();
    this.changeHandler();

    this.block.addEventListener('click', this.clickHandler);
    this.block.addEventListener('change', this.changeHandler);

    this.promiseResolve(this);
    this.block.AsyncCounter = this;
  };

  setOptions = () => {
    this.input = this.block.querySelector('.counter__input');
    if (!this.input) return this.BlockError.error('Отсутствует counter__input');

    this.plus = this.block.querySelector('[data-control-plus]');
    if (!this.plus) return this.BlockError.error('Отсутствует [data-control-plus]');

    this.minus = this.block.querySelector('[data-control-minus]');
    if (!this.minus) return this.BlockError.error('Отсутствует [data-control-minus]');

    this.mask = this.input.dataset['inputmask'] || '"alias": "numeric", "rightAlign": false';

    this.min = toNumber(this.block.dataset['min']);
    this.min = isNull(this.min) || isNaN(this.min) ? -Infinity : this.min;

    this.max = toNumber(this.block.dataset['max']);
    this.max = isNull(this.max) || isNaN(this.max) ? Infinity : this.max;

    this.step = toNumber(this.block.dataset['step']);
    this.step = isNull(this.step) || isNaN(this.step) ? 1 : this.step;

    this.float = toNumber(this.block.dataset['float']);
    this.float = isNull(this.float) || isNaN(this.float) ? 0 : this.float;

    this.roundByStep = this.block.hasAttribute('data-round-by-step');
  };

  clickHandler = ({ target }) => {
    if (target.closest('[data-control-minus]')) {
      this.updateCount(-this.step);
    } else if (target.closest('[data-control-plus]')) {
      this.updateCount();
    }
  };

  updateCount = (val = this.step) => {
    const newVal = (toNumber(this.input.value) + toNumber(val)).toFixed(this.float);

    if (newVal >= this.max) {
      this.input.value = this.max;
    } else if (newVal <= this.min) {
      this.input.value = this.min;
    } else {
      this.input.value = newVal;
    }

    this.input.dispatchEvent(new Event('change', {bubbles: true}));
  };

  changeHandler = () => {
    this.plus.removeAttribute('disabled');
    this.minus.removeAttribute('disabled');

    const val = toNumber(this.input.value);

    if (this.roundByStep) this.input.value = this.getClosest(toNumber(val));

    if (val >= this.max) this.plus.setAttribute('disabled', '');
    if (val <= this.min) this.minus.setAttribute('disabled', '');

    this.block.classList.toggle('disabled', this.input.disabled);
    if (this.input.disabled) {
			this.plus.setAttribute('disabled', '');
			this.minus.setAttribute('disabled', '');
		}
  };

  getClosest = (value) => {
    return (Math.round(value / this.step) * this.step).toFixed(this.float);
  };

  updateMask = () => {
    const newMask = this.mask + `${this.max === Infinity ? '' : `, "max": ${this.max}`} ${this.min === -Infinity ? '' : `, "min": ${this.min}`}, "digits": ${this.float}, "allowMinus": ${this.min < 0}`;
    this.input.setAttribute('data-inputmask', newMask);
    window.Block.Inputmask().mask(this.input);
  };

  /***
   * Обновляет параметры каунтера и маски валидации. Вызывает событие change
   * @param step {number|undefined} - шаг
   * @param max {number|undefined} - максимальное значение
   * @param min {number|undefined} - минимальное значение
   * @param float {number|undefined} - цифр после запятой
   * @param mask {string} - новый начальный шаблон маски, которому будут добавляться параметры
   * @param roundByStep {boolean|undefined} - нужно ли округление до шага после ручного ввода пользователя
   */
  update = ({ step, max, min, float, mask, roundByStep }) => {
    this.step = isUndefined(step) ? this.step : toNumber(step);
    this.max =  isUndefined(max) ? this.max : toNumber(max);
    this.min =  isUndefined(min) ? this.min : toNumber(min);
    this.float = isUndefined(float) ? this.float : toNumber(float);
    this.mask = mask ? mask : this.mask;
    this.roundByStep = isUndefined(roundByStep) ? this.roundByStep : roundByStep;

    this.updateMask();
    this.input.dispatchEvent(new Event('change', {bubbles: true}));
  };

  destroy = () => {
    if (this.LazyInit) this.LazyInit.destroy();
    if (this.block) {
      this.block.removeEventListener('click', this.clickHandler);
      this.block.removeEventListener('change', this.changeHandler);
      this.block.classList.remove('counter_inited');
      delete this.block.Counter;
      delete this.block.AsyncCounter;
    }
  };

  static init(container = document, force = false) {
    Array.from(container.querySelectorAll('.counter:not(.counter_inited)')).forEach((elem) => new Counter(elem, force));
  }
}

document.addEventListener('DOMContentLoaded',() => Counter.init());

// Экспорт;
window.Block.Counter = Counter;
